import React, { useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'store/hooks';
import IconButton from '@mui/material/IconButton';
import WithLoader from 'ui/WithLoader/WithLoader';
import {
    BudgetProposalT,
    CellRendererParams,
    InscriptionWithMetadata,
} from 'routes/BudgetModule/config/types';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import DeleteIcon from '@mui/icons-material/Delete';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { getInscriptionInformation } from '../dataHelpers';
import { useDeleteInscription } from '../hooks/inscriptions';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';

const StyBox = styled(Box, {
    name: 'StyBox',
})({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

type ClassifyButtonCellRendererParams =
    CellRendererParams<InscriptionWithMetadata> & {
        budgetProposals: BudgetProposalT[];
        inscriptionIdColumn?: keyof InscriptionT;
        queryCacheKey: InscriptionQueryKey;
    };

export default function DeleteButtonCellRenderer({
    data,
    budgetProposals,
    inscriptionIdColumn,
    queryCacheKey,
}: ClassifyButtonCellRendererParams): React.ReactElement {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const statusesSettings = useContext(StatusSettingsContext);
    const { inscriptionId, canEdit, budgetProposal } =
        getInscriptionInformation(
            data,
            budgetProposals,
            statusesSettings,
            inscriptionIdColumn,
        );

    const deleteInscriptionMutation = useDeleteInscription(
        queryCacheKey,
        dispatch,
        queryClient,
        inscriptionId,
        inscriptionIdColumn,
    );

    if (!data || budgetProposal === undefined) {
        return <span />;
    }
    return (
        <StyBox>
            <IconButton
                color="primary"
                style={{ padding: '0px' }}
                disabled={!canEdit || deleteInscriptionMutation.isLoading}
                onClick={() => {
                    setShowConfirmDeleteModal(true);
                }}
                size="large"
            >
                <DeleteIcon />
            </IconButton>
            <WithLoader isLoading={deleteInscriptionMutation.isLoading}>
                <ConfirmDeleteModal
                    show={showConfirmDeleteModal}
                    cancel={() => {
                        setShowConfirmDeleteModal(false);
                    }}
                    confirm={() => {
                        setShowConfirmDeleteModal(false);
                        deleteInscriptionMutation.mutate();
                    }}
                    title={t('budgetProposal.inscriptionDeleteConfirm')}
                />
            </WithLoader>
        </StyBox>
    );
}
